import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mediaMobileOnly,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {FONT_SIZES} from '../../../constants/typography';
import {useInView} from '../../../hooks/useInView/useInView';
import {ImageType} from '../../../types/Image';
import {FixedRatioContainer} from '../../atoms/FixedRatioContainer/FixedRatioContainer';
import {Image} from '../../atoms/Image/Image';
import {Heading16} from '../../atoms/Text/Headings';
import {Paragraph14} from '../../atoms/Text/Paragraphs';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';

export type textPosition = 'on-image' | 'below-image';

export interface SliderTileProps {
  testId: number;
  headline: string;
  subline: string;
  to: string;
  image: ImageType;
  className?: string;
  textsPosition: textPosition;
  imageRatio: `${number}:${number}`;
  ctaLabel?: string;
}

export const SliderTile: React.FC<SliderTileProps> = ({
  headline,
  subline,
  image,
  to,
  testId,
  textsPosition,
  imageRatio,
  className,
  ctaLabel,
}) => {
  const {t} = useTranslation();
  const {ref, inView} = useInView<HTMLAnchorElement>({
    threshold: 0.5,
  });

  return (
    <SliderLink
      data-testid={`sliderTile-${testId}`}
      className={className}
      to={to}
      ref={ref}
      data-inview={inView ? 'true' : 'false'}
      data-text-position={textsPosition}
      aria-label={undefined}
    >
      <FixedRatioContainer ratio={imageRatio}>
        <BackgroundModern
          loading="lazy"
          importance="low"
          src={image.url}
          alt={image.description}
          breakpoints={breakpoints}
          widths={possibleImageWidths}
          ratio={imageRatio}
          data-testid="sliderBackgroundWrapper"
        />
      </FixedRatioContainer>
      <TextWrapper>
        <ImageTextWrapper>
          <DiscoverLink data-testid="discoverLink">
            {ctaLabel || t('Discover')}
          </DiscoverLink>
        </ImageTextWrapper>

        <Headline
          data-testid="headline"
          title={headline}
          textsPosition={textsPosition}
        >
          {headline}
        </Headline>
        <Subline
          data-testid="subline"
          title={subline}
          textsPosition={textsPosition}
        >
          {subline}
        </Subline>
      </TextWrapper>
    </SliderLink>
  );
};

const breakpoints = [
  {
    breakpoint: '(max-width: 767px)',
    columns: 2,
  },
  {
    breakpoint: '(max-width: 1023px)',
    columns: 3,
  },
  {
    columns: 4,
  },
];
const possibleImageWidths = [600, 800, 960, 1200];

const ImageTextWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  transform: translate(-16px, -10%);
`;

const Headline = styled(Heading16)<{
  textsPosition: SliderTileProps['textsPosition'];
}>`
  hyphens: auto;
  color: ${COLORS.Black};
  z-index: 1;
  width: 100%;
  transition: transform 0.2s ease-out;
  font-weight: bold;
  overflow: hidden;

  font-size: ${FONT_SIZES.px16};
  line-height: ${FONT_SIZES.px24};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
  `)}

  [data-text-position='on-image'] & {
    font-size: ${FONT_SIZES.px24};
    text-align: center;

    ${tabletAndUpLinaria(`
      font-size: ${FONT_SIZES.px32};
    `)}
  }
`;

const Subline = styled(Paragraph14)<{
  textsPosition: SliderTileProps['textsPosition'];
}>`
  color: ${COLORS.Grey1};
  z-index: 1;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: transform 0.2s ease-out;

  font-size: ${FONT_SIZES.px14};
  margin-block: 0.25rem;

  ${tabletAndUpLinaria(`
    font-size: 13px;
    margin-top: 0.75rem;
    margin-bottom: unset;
    line-height: 1.4;
  `)}

  [data-text-position='on-image'] & {
    font-size: ${FONT_SIZES.px12};
    text-transform: uppercase;
    text-align: center;
  }
`;

const TextWrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 20px 16px 0px;
  box-sizing: border-box;

  ${mobileOnlyLinaria(`
    padding: 8px 16px 0px;
    text-align: center;
  `)};

  [data-text-position='on-image'] & {
    position: absolute;
    top: 0;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column-reverse;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.1);
    text-align: unset;

    ${Headline} {
      color: ${COLORS.White};
      line-height: 1.25;
    }

    ${Subline} {
      margin: 0 0 8px 0;
      color: ${COLORS.White};
    }

    @media (hover: hover) {
      ${ImageTextWrapper} {
        transform: translate(0, 10%);
      }
    }
  }
`;

const DiscoverLink = styled(Heading16)`
  font-weight: 500;
  border-bottom: 1px solid ${COLORS.White};
  opacity: 0;
  transition: opacity 300ms ease;
  color: ${COLORS.White};
  z-index: 1;
  margin-top: 20px;
`;

const ShadeFallback = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.3);
`;

const BackgroundModern = styled(Image)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  object-fit: cover;
  object-position: center 75%;
  border-radius: 4px;
  filter: brightness(70%);
`;

const SliderBackgroundWrapper = styled.div`
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center 75%;
  transition: transform 300ms ease;
  transform-origin: center;
  height: 100%;
  width: 100%;
  border-radius: 4px;
  top: 1.5%;
  will-change: transform;

  > ${BackgroundModern} {
    opacity: 0;
  }

  @supports (object-fit: cover) {
    background-image: none !important;
    > ${BackgroundModern} {
      opacity: 1;
    }
    > ${ShadeFallback} {
      display: none;
    }
  }
`;

const SliderLink = styled(UnstyledLink)`
  display: flex;
  flex-direction: column;
  height: auto;
  position: relative;
  cursor: pointer;
  transition: filter 300ms ease;
  margin: 5px;
  transition: transform 300ms ease;
  will-change: transform;

  ${mobileOnlyLinaria(`
    transform: scale(0.97, 0.92);
  `)}

  &[data-inview='true'] {
    ${mobileOnlyLinaria(`
      transform: scale(1);
    `)}

    @media ${mediaMobileOnly} {
      ${SliderBackgroundWrapper} {
        filter: drop-shadow(0 5px 5px rgba(0, 0, 0, 0.3));
      }
    }
  }

  @media (hover: hover) {
    &:hover {
      // there's a glitch on chrome when the size of the element inside of a snap scroll get resizes while scrolling the scroll glitches
      // This transform should be uncommented once we can overcome this issue
      /* transform: scale(1.03); */

      ${SliderBackgroundWrapper} {
        /* transform: scale(1.03); */
        filter: drop-shadow(0 5px 5px rgba(0, 0, 0, 0.3));
      }

      ${DiscoverLink} {
        opacity: 1;
      }

      &[data-text-position='on-image'] {
        ${Headline} {
          transform: translate(0, -50px);
        }
        ${Subline} {
          transform: translate(0, -70px);
        }
      }
    }
  }
`;
